/**
 * Centralized API Endpoints Configuration
 * Single source of truth for all API URLs across the application
 */

import { API_BASE_URL } from "config/api";

export { API_BASE_URL };

/**
 * All API Endpoints
 * Organized by domain/module for easy navigation
 */
export const ENDPOINTS = {
  /**
   * MATERIALS / CHARTS MODULE
   * Endpoints for material data, charts, and analytics
   */
  MATERIALS: {
    /**
     * Get all materials list
     * Returns: Array of materials with id, code, name, description, last_value, recent_chart_data, etc.
     */
    LIST: '/materials',

    /**
     * Lightweight materials list for compare dropdowns
     * Returns: Array of materials without expensive chart payloads
     */
    COMPARE_OPTIONS: '/materials/compare/options',

    /**
     * Search materials (text or AI-powered)
     * Query params: query (string, required), type (text|ai, default=text), limit (1-100, default=20)
     * Returns: Array of materials (type=text) or Object with results + ai_summary (type=ai)
     */
    SEARCH: '/materials/search',

    /**
     * Get all sectors
     * Returns: Array of sectors with id, name, image
     */
    SECTORS: '/sectors/',

    /**
     * Get top sectors for authenticated user
     * Returns: Array of top sectors with id, name, image, score (personalized for user)
     * Query params: limit (default 4, max 10)
     */
    TOP_SECTORS: '/materials/me/top_sectors',

    /**
     * Get recently viewed materials for authenticated user
     * Returns: Array of recently viewed materials with id, code, name, viewed_at, time_series
     * Query params: limit (optional)
     */
    RECENT_VIEWS: '/materials/me/recent_views',

    /**
     * General material information
     * Returns: name, description, last_price, currency, VAT, change%, etc.
     */
    GENERAL: (materialCode) => `/materials/${materialCode}/general`,

    /**
     * Chart overview - 6 years of historical monthly data
     * Returns: Array of monthly data points (Oct 2019 - Sep 2025)
     */
    CHART_OVERVIEW: (materialCode) => `/materials/${materialCode}/chart/overview`,

    /**
     * Chart series with timeframe support
     * Query params: start={date}&end={date}&tf={timeframe}[&currency=EUR&unit=kg]
     * Returns: { data: OHLCV points, meta: { source_currency, source_unit, currency, unit,
     *   converted, currency_converted, unit_converted, fx_basis, fx_series, points_without_rate, reason } }
     * currency/unit rechnet das Backend mit dem Kurs des jeweiligen Tages um.
     */
    CHART_SERIES: (materialCode) => `/materials/${materialCode}/chart/series`,

    /**
     * Chart comparison - compare multiple materials over the same time window
     * Query params: codes={comma-separated codes}&start={ISO datetime}&end={ISO datetime}&tf={timeframe}[&currency&unit]
     * Returns: series object with material codes as keys, each containing OHLCV data,
     * plus meta per code (same shape as chart/series meta)
     * Example: { "series": { "MAT_1": [...], "MAT_2": [...] }, "meta": { "MAT_1": {...} } }
     */
    CHART_COMPARE: (materialCode) => `/materials/${materialCode}/chart/compare`,

    /**
     * Export chart/price data for a material
     * Query params: start, end, tf, frequency, sort, include_forecast, include_news, currency, unit
     */
    CHART_EXPORT: (materialCode) => `/materials/${materialCode}/chart/export`,

    /**
     * Assessment metrics
     * Returns: commodity_necessity, risk_assessment, recycling_rate (all with values + descriptions)
     */
    ASSESSMENT: (materialCode) => `/materials/${materialCode}/assessment`,

    /**
     * Availability data (on/off production)
     * Returns: data array with year/on/off values, onValue, offValue, description
     */
    AVAILABILITY: (materialCode) => `/materials/${materialCode}/availability`,

    /**
     * Country production distribution
     * Returns: Array of countries with share%, value, change
     */
    COUNTRY_PRODUCTION: (materialCode) => `/materials/${materialCode}/country_production`,

    /**
     * Market stability by country
     * Returns: Array of countries with stability% and descriptions
     */
    MARKET_STABILITY: (materialCode) => `/materials/${materialCode}/market_stability`,

    /**
     * Need projection (10-year forecast)
     * Returns: Array of yearly projections with lowEstimate, averageEst, highEstimate + description
     */
    NEED_PROJECTION: (materialCode) => `/materials/${materialCode}/need_projection`,

    /**
     * Recent daily prices (last 5 days)
     * Returns: Array of daily data with date, change%, price
     */
    RECENT_DAILY: (materialCode) => `/materials/${materialCode}/recent_daily`,

    /**
     * Rolling average series (daily, weekly, monthly, yearly)
     * Returns: days[], weeks[], months[], years[] arrays with averages
     */
    ROLLING_AVERAGE: (materialCode) => `/materials/${materialCode}/rolling_average_series`,

    /**
     * Forecast series (simple projection)
     * Query params: horizon (1w|1m|3m), frequency (daily|weekly), include_news, scenario
     */
    FORECAST: (materialCode) => `/materials/${materialCode}/forecast`,
  },

  /**
   * REFERENCE DATA
   * Oeffentliche, cachebare Stammdaten (ETag) fuer die Chart-Umrechnung
   */
  REFERENCE: {
    /** Returns: Array<{ code, factor, base_code, name_de, name_en, name_zh, visible, sort_idx, convertible }> */
    CURRENCIES: '/reference/currencies',
    /** Returns: Array<{ code, name_de, name_en, name_zh, symbol, dimension, to_base, convertible }> */
    UNITS: '/reference/units',
  },

  /**
   * SECTORS MODULE
   * Endpoints for sector data and sector materials
   */
  SECTORS: {
    /**
     * Get materials by sector ID
     * Returns: lists array with materials grouped by list name
     */
    MATERIALS: (sectorId) => `/sectors/${sectorId}/materials`,
  },

  /**
   * NEWS MODULE (Future Implementation)
   * Endpoints for news articles, matching, and search
   */
  NEWS: {
    /**
     * Get news list with pagination
     * Query params: page, limit, category, sort
     */
    LIST: '/news',

    /**
     * Get news detail by ID
     */
    DETAIL: (newsId) => `/news/${newsId}`,

    /**
     * Get matching/personalized news
     */
    MATCHING: '/news/matching',

    /**
     * Search news
     * Query params: q (query), filters
     */
    SEARCH: '/news/search',

    /**
     * News categories
     */
    CATEGORIES: '/news/categories',

    /**
     * Top/hot news
     */
    HOT: '/news/hot',
  },

  /**
   * DASHBOARD MODULE (Future Implementation)
   * Endpoints for dashboard stats and widgets
   */
  DASHBOARD: {
    /**
     * Dashboard overview statistics
     * Returns: KPIs, summary cards data
     */
    STATS: '/dashboard/stats',

    /**
     * Recent activity feed
     * Returns: Timeline of recent actions
     */
    RECENT_ACTIVITY: '/dashboard/activity',

    /**
     * Active alerts/notifications for dashboard
     */
    ALERTS: '/dashboard/alerts',

    /**
     * Widget data (customizable)
     */
    WIDGETS: (widgetId) => `/dashboard/widgets/${widgetId}`,
  },

  /**
   * REPORTS MODULE
   */
  REPORTS: {
    LIST: '/reports',
    RECENTS: '/reports/me/recents',
    MATCHING: '/reports/me/matching',
    FIELDS: '/reports/fields',
    FIELD_REPORTS: (fieldId) => `/reports/fields/${fieldId}/reports`,
    SOURCES: '/reports/sources',
    SOURCE_REPORTS: (sourceId) => `/reports/sources/${sourceId}/reports`,
    SECTORS: '/reports/sectors',
    SEARCH: '/reports/search',
    DETAIL: (reportId) => `/reports/${reportId}`,
    GENERATE: '/reports/generate',
    CUSTOM_CREATE: '/reports/custom',
    CUSTOM_LIST: '/reports/custom',
    CUSTOM_DETAIL: (reportId) => `/reports/custom/${reportId}`,
    CUSTOM_RETRY: (reportId) => `/reports/custom/${reportId}/retry`,
    CUSTOMIZATION: (reportId) => `/reports/${reportId}/customizations`,
    CUSTOMIZATION_DETAIL: (customizationId) => `/reports/customizations/${customizationId}`,
    ENHANCEMENT: (customizationId) => `/reports/customizations/${customizationId}/enhancements`,
    ENHANCEMENT_DETAIL: (enhancementId) => `/reports/enhancements/${enhancementId}`,
  },

  /**
   * COMPANIES MODULE (Future Implementation)
   * Endpoints for company information and search
   */
  COMPANIES: {
    /**
     * Get companies list with pagination
     * Query params: page, limit, sector, sort
     */
    LIST: '/companies',

    /**
     * Get company detail by ID
     */
    DETAIL: (companyId) => `/companies/${companyId}`,

    /**
     * Search companies
     * Query params: q (query), filters
     */
    SEARCH: '/companies/search',

    /**
     * Company sectors/industries
     */
    SECTORS: '/companies/sectors',
  },

  /**
   * INSIGHTS MODULE (Future Implementation)
   * Endpoints for market insights and analytics
   */
  INSIGHTS: {
    /**
     * Get insights list with pagination
     * Query params: page, limit, category, sort
     */
    LIST: '/insights',

    /**
     * Get insight detail by ID
     */
    DETAIL: (insightId) => `/insights/${insightId}`,

    /**
     * Insights categories
     */
    CATEGORIES: '/insights/categories',

    /**
     * Featured/trending insights
     */
    FEATURED: '/insights/featured',
  },

  /**
   * USER / AUTHENTICATION MODULE (Future Implementation)
   * Endpoints for user profile, settings, and auth
   */
  USER: {
    /**
     * User profile information
     */
    PROFILE: '/user/profile',

    /**
     * User settings
     */
    SETTINGS: '/user/settings',

    /**
     * User notifications
     * Query params: page, limit, unread
     */
    NOTIFICATIONS: '/user/notifications',

    /**
     * Mark notification as read
     */
    MARK_NOTIFICATION_READ: (notificationId) => `/user/notifications/${notificationId}/read`,

    /**
     * User preferences
     */
    PREFERENCES: '/user/preferences',

    /**
     * Login
     */
    LOGIN: '/auth/login',

    /**
     * Logout
     */
    LOGOUT: '/auth/logout',

    /**
     * Register
     */
    REGISTER: '/auth/register',

    /**
     * Password reset
     */
    RESET_PASSWORD: '/auth/reset-password',
  },

  /**
   * ADMIN MODULE (Future Implementation)
   * Endpoints for admin panel functionality
   */
  ADMIN: {
    /**
     * Admin dashboard stats
     */
    DASHBOARD: '/admin/dashboard',

    /**
     * User management
     */
    USERS: '/admin/users',
    USER_DETAIL: (userId) => `/admin/users/${userId}`,

    /**
     * Content management
     */
    CONTENT: '/admin/content',

    /**
     * System settings
     */
    SETTINGS: '/admin/settings',

    /**
     * Logs and analytics
     */
    LOGS: '/admin/logs',

    /**
     * Stammdaten-Arbeitsmappe (Materialien, Sektoren, Listen, Zuordnungen)
     * EXPORT: GET, liefert .xlsx
     * IMPORT: POST multipart `file`; mit ?dry_run=true nur Vorschau (wird zurückgerollt)
     */
    MATERIAL_WORKBOOK_EXPORT: '/admin/data/materials/workbook/export',
    MATERIAL_WORKBOOK_IMPORT: '/admin/data/materials/workbook/import',
  },

  /**
   * ASSISTANT MODULE
   * Endpoints for the AI Assistant chat panel
   */
  ASSISTANT: {
    /**
     * Send a message to the AI assistant.
     * POST body: { message: string, context?: string }
     * Returns: { reply: string }
     */
    CHAT: '/assistant/chat',
  },

  /**
   * INTERACTIONS MODULE
   * Endpoints for tracking user interactions
   */
  INTERACTIONS: {
    /**
     * Track resource view
     * POST /interactions/views
     * Body: { resource_type: string, resource_id: string }
     */
    TRACK_VIEW: '/interactions/views',
  },

  /**
   * PRODUCTS MODULE (Models)
   * Endpoints for product models, CRUD, and analytics
   */
  PRODUCTS: {
    /**
     * List all products with pagination
     * Query params: limit (1-200, default=50), offset (>=0, default=0)
     * Returns: { items[], total, limit, offset, has_more }
     */
    LIST: '/products',

    /**
     * Product overview blocks (recent views, your models, related fields, sectors)
     * Query params: limit_recent (1-20, default=8), limit_models (1-200, default=30)
     * Returns: { has_models, recent_views[], your_models[], demo_models[], related_fields[], all_sectors[] }
     */
    OVERVIEW: '/products/overview',

    /**
     * Recently viewed products for authenticated user
     * Query params: limit (optional)
     */
    RECENT_VIEWS: '/products/me/recent_views',

    /**
     * Related product sectors for authenticated user
     */
    RELATED_SECTORS: '/products/me/related_sectors',

    /**
     * List product sectors with counts
     * Query params: limit (1-500, default=200)
     * Returns: Array<{ id, name, slug, image_url, product_count }>
     */
    SECTORS: '/products/sectors',

    /**
     * List products in a sector
     */
    SECTOR_PRODUCTS: (sectorId) => `/products/sectors/${sectorId}`,

    /**
     * List sector lists and products (includes No List)
     */
    SECTOR_LISTS: (sectorId) => `/products/sectors/${sectorId}/lists`,

    /**
     * Search products by name (own + demo)
     * Query params: query (string, required), limit (1-200, default=50)
     * Returns: { query, results: Array<{ product, sectors, is_owner }> }
     */
    SEARCH: '/products/search',

    /**
     * Product search for selection (name + price)
     * Query params: query (string, required)
     */
    SEARCH_OPTIONS: '/products/search/options',

    /**
     * Prompt-style (AI) product search
     * POST body: { query: string }
     * Query params: limit (1-200, default=50)
     * Returns: { query, results: Array<{ product, sectors, is_owner }> }
     */
    PROMPT_SEARCH: '/products/prompt',

    /**
     * Create a product model
     * POST body: { name, description, image_url, default_currency, base_unit, base_quantity,
     *   sector_ids: number[], components: Array<{ component_type, material_id, product_ref_id,
     *   name, quantity, unit, currency, source_country, destination_country, sort_order }> }
     * Returns: Full product object with id, sectors, components
     */
    CREATE: '/products',

    /**
     * Create a product model with nested sub-products
     */
    CREATE_NESTED: '/products/nested',

    /**
     * Get product detail including sectors and components
     */
    DETAIL: (productId) => `/products/${productId}`,

    /**
     * Update a product model (partial updates — send only changed fields)
     */
    UPDATE: (productId) => `/products/${productId}`,

    /**
     * Update a product model with nested sub-products
     */
    UPDATE_NESTED: (productId) => `/products/${productId}/nested`,

    /**
     * Delete a product model (owner only)
     * Returns: { ok: true }
     */
    DELETE: (productId) => `/products/${productId}`,

    /**
     * Copy a product model into current user's workspace
     */
    COPY: (productId) => `/products/${productId}/copy`,

    /**
     * Upload a product image
     * POST body: multipart/form-data with field "image_file"
     */
    IMAGE: (productId) => `/products/${productId}/image`,

    /**
     * Core product info + last price aggregation
     */
    GENERAL: (productId) => `/products/${productId}/general`,

    /**
     * Monthly overview chart series
     */
    CHART_OVERVIEW: (productId) => `/products/${productId}/chart/overview`,

    /**
     * OHLC series for a given time window
     */
    CHART_SERIES: (productId) => `/products/${productId}/chart/series`,

    /**
     * Compare multiple product ids
     */
    CHART_COMPARE: (productId) => `/products/${productId}/chart/compare`,

    /**
     * Latest daily aggregates
     */
    RECENT_DAILY: (productId) => `/products/${productId}/recent_daily`,

    /**
     * Rolling averages (7d/7w/7y)
     */
    ROLLING_AVERAGES: (productId) => `/products/${productId}/rolling_averages`,

    /**
     * Bucketed rolling averages series
     */
    ROLLING_AVERAGE_SERIES: (productId) => `/products/${productId}/rolling_average_series`,

    /**
     * Table rows with change + weekly/monthly/yearly averages
     */
    TABLE_AVERAGE: (productId) => `/products/${productId}/table_average`,

    /**
     * Regulatory entries (GET list, POST create)
     */
    REGULATORY: (productId) => `/products/${productId}/regulatory`,

    /**
     * Single regulatory entry (GET, PUT, DELETE)
     */
    REGULATORY_ENTRY: (productId, entryId) => `/products/${productId}/regulatory/${entryId}`,

    /**
     * Product insights feed (GET list, POST create)
     */
    INSIGHTS: (productId) => `/products/${productId}/insights`,

    /**
     * Single insight entry (GET, PUT, DELETE)
     */
    INSIGHT_ENTRY: (productId, entryId) => `/products/${productId}/insights/${entryId}`,

    /**
     * Sector breakdown for materials in the product
     */
    CLASSIFICATION: (productId) => `/products/${productId}/classification`,

    /**
     * Origin country breakdown for materials in the product
     */
    SOURCES: (productId) => `/products/${productId}/sources`,

    /**
     * Risk score and text (GET, POST create, PUT update, DELETE)
     */
    RISK: (productId) => `/products/${productId}/risk`,
  },
};

/**
 * Helper function to build query strings
 * @param {Object} params - Query parameters object
 * @returns {string} Query string (e.g., "?page=1&limit=10")
 */
export const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';

  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return queryString ? `?${queryString}` : '';
};

/**
 * Helper function to build full URL with query params
 * @param {string} endpoint - Base endpoint
 * @param {Object} params - Query parameters
 * @returns {string} Full endpoint with query string
 */
export const buildEndpoint = (endpoint, params = {}) => {
  return `${endpoint}${buildQueryString(params)}`;
};

export default ENDPOINTS;
